import { useEffect, useMemo, useState } from 'react';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import { Eye, RotateCcw } from 'lucide-react';
import VehicleLayoutSvg from './VehicleLayoutSvg';
import { buildVehicleGeometry } from './vehicleDiagram/layoutGeometry';
import { STATE_ORDER, statusOf, TYRE_STATES } from './vehicleDiagram/diagramTheme';
import { describePosition } from './vehicleDiagram/positionDescription';
import webglAvailable from './vehicleDiagram/scene3d/webglAvailable';
import { positionCode } from './vehicleModels/domain/layout';
import { resolveRenderCoverage } from './vehicleModels/domain/renderCoverage';
import { resolveRenderIdentity } from './vehicleModels/domain/renderIdentity';
import { findPresetForRenderIdentity } from './vehicleModels/presets/registry';
import { VehicleScene } from './vehicleModels/scene/VehicleScene';

const positionRef = (axle, tyre) => ({
  axle: axle.index + 1,
  side: tyre.side,
  ...(axle.dual ? { slot: tyre.slot === 0 ? 'outer' : 'inner' } : {}),
});

const serverPositionRef = (position) => ({
  axle: Number(position.axle_index),
  side: position.side,
  ...(position.fitment_type === 'outer' || position.fitment_type === 'inner'
    ? { slot: position.fitment_type }
    : {}),
});

const createSceneData = (configuration, vehicle) => {
  const geometry = buildVehicleGeometry(configuration, vehicle);
  if (!geometry) return null;

  const layout = {
    axles: geometry.axles.map((axle) => ({
      index: axle.index + 1,
      fitment: axle.dual ? 'dual' : 'single',
    })),
  };
  const sceneVehicle = {
    layout,
    positionRefs: (configuration?.positions || []).map(serverPositionRef),
    tyres: {},
  };
  if (!sceneVehicle.positionRefs.length) {
    sceneVehicle.positionRefs = geometry.tyres.map((tyre) => positionRef(
      geometry.axles[tyre.axleIndex],
      tyre,
    ));
  }
  const positionByCode = new Map();
  const refById = new Map();

  geometry.axles.forEach((axle) => {
    axle.tyres.forEach((tyre) => {
      const ref = positionRef(axle, tyre);
      const code = positionCode(ref);
      sceneVehicle.tyres[code] = tyre.position.current_fitment || null;
      positionByCode.set(code, tyre.position);
      refById.set(String(tyre.position.id), ref);
    });
  });

  const identity = resolveRenderIdentity(configuration);
  const coverage = resolveRenderCoverage(
    identity,
    sceneVehicle,
    findPresetForRenderIdentity,
  );

  return {
    geometry,
    identity,
    coverage,
    preset: coverage.preset,
    sceneVehicle,
    positionByCode,
    refById,
  };
};

const specLine = (geometry) => {
  const details = [
    `${geometry.positionCount} wheels`,
    `${geometry.axleCount} ${geometry.axleCount === 1 ? 'axle' : 'axles'}`,
  ];
  if (geometry.axles.some((axle) => axle.dual)) details.push('dual fitment');
  return details.join(' · ');
};

const ClaudeVehicleLayout = ({ vehicle, configuration, selectedPositionId, onSelectPosition }) => {
  const data = useMemo(
    () => createSceneData(configuration, vehicle),
    [configuration, vehicle]
  );
  const [hovered, setHovered] = useState();
  const [sceneKey, setSceneKey] = useState(0);
  const [chassisView, setChassisView] = useState(false);

  useEffect(() => {
    setHovered(undefined);
    setChassisView(false);
  }, [configuration, vehicle]);

  const counts = useMemo(() => {
    const tally = Object.fromEntries(STATE_ORDER.map((key) => [key, 0]));
    (configuration?.positions || []).forEach((position) => {
      const key = statusOf(position);
      tally[key] = (tally[key] || 0) + 1;
    });
    return tally;
  }, [configuration]);

  const webgl = webglAvailable();
  if (data?.coverage?.state !== 'exact_3d' || !webgl) {
    return (
      <VehicleLayoutSvg
        vehicle={vehicle}
        configuration={configuration}
        selectedPositionId={selectedPositionId}
        onSelectPosition={onSelectPosition}
        renderCoverage={data?.coverage}
        renderFallbackReason={data?.coverage?.state === 'exact_3d' && !webgl ? 'webgl_unavailable' : undefined}
      />
    );
  }

  const selected = data.refById.get(String(selectedPositionId));
  const hoveredPosition = hovered && data.positionByCode.get(positionCode(hovered));
  const selectRef = (ref) => {
    const position = data.positionByCode.get(positionCode(ref));
    if (position) onSelectPosition(position);
  };

  return (
    <ClassNames>
      {({ css }) => (
        <figure
      className="tm-plate tm-plate--3d tm-plate--claude"
      data-render-source={data.identity.source}
      data-render-coverage={data.coverage.state}
      data-render-model-key={data.identity.renderModelKey}
      data-render-layout-key={data.identity.renderLayoutKey}
    >
      <figcaption className="tm-plate__head">
        <span className="tm-plate__spec">{specLine(data.geometry)}</span>
        <span className="tm-plate__hint">{data.preset.label} · Interactive 3D</span>
      </figcaption>

      <div
        className="tm-plate__stage tm-plate__stage--3d"
        role="group"
        aria-label={`${vehicle?.registration_num || configuration?.vehicle_number || 'Vehicle'} interactive 3D vehicle. Drag to rotate and select a wheel.`}
      >
        <VehicleScene
          key={`${data.preset.id}-${sceneKey}`}
          preset={data.preset}
          vehicle={data.sceneVehicle}
          hovered={hovered}
          selected={selected}
          chassisView={chassisView}
          onHover={setHovered}
          onSelect={selectRef}
        />
        {hoveredPosition && (
          <p className="tm-plate__tyre-tooltip" role="tooltip">
            {describePosition(hoveredPosition)}
          </p>
        )}
        <button
          type="button"
          className="tm-plate__chassis"
          aria-pressed={chassisView}
          onClick={() => setChassisView((current) => !current)}
        >
          <Eye aria-hidden="true" /> Chassis view
        </button>
        <button
          type="button"
          className="tm-plate__reset"
          onClick={() => setSceneKey((current) => current + 1)}
        >
          <RotateCcw aria-hidden="true" /> Reset view
        </button>
        <p className="tm-plate__canvas-hint">
          {chassisView
            ? 'Body ghosted · all wheels selectable · drag up for plan view'
            : 'Drag to orbit · Scroll or pinch to zoom · Select a wheel'}
        </p>
      </div>

          <ul className="tm-plate__legend">
            {STATE_ORDER.map((key) => (
              <li key={key} className="tm-plate__legend-item" data-zero={counts[key] ? undefined : true}>
                <span className={`tm-plate__legend-dot ${css({ backgroundColor: TYRE_STATES[key].color })}`} />
                <span className="tm-plate__legend-label">{TYRE_STATES[key].label}</span>
                <span className="tm-plate__legend-count">{counts[key]}</span>
              </li>
            ))}
          </ul>
        </figure>
      )}
    </ClassNames>
  );
};

ClaudeVehicleLayout.propTypes = {
  vehicle: PropTypes.object,
  configuration: PropTypes.object,
  selectedPositionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectPosition: PropTypes.func.isRequired,
};

export default ClaudeVehicleLayout;
